import React from 'react'
import { SxProps } from '@mui/material'
import { PersonOutline as PersonOutlineIcon } from '@mui/icons-material'

import { localizedStrings } from '@core/strings'
import { useGetPermitTemplateRolesByProject } from '@core/react-query/features/templates/permitTemplates'
import { PermitTemplateRole } from '@core/entities/permit/PermitTemplateRoles'
import { Box, Flex, Text } from '@fundamentals'
import { Card } from '@common/material'
import { Section } from '@common/Section'

export type SummaryParticipant = {
  firstName: string
  lastName: string
  company?: string
  roleId: number
}

type BriefingParticipantsSummaryProps = {
  participants: SummaryParticipant[]
  templateId?: number
  projectId?: number
  sx?: SxProps
}

// Read-only list of the participants that will be added to the permit, with
// their role — shown in the review step and at the end of the permit-details
// questions tab. Role names are resolved from the project-scoped roles
// endpoint (current version); an unresolved role just omits the suffix.
export const BriefingParticipantsSummary: React.FC<
  BriefingParticipantsSummaryProps
> = ({ participants, templateId, projectId, sx }) => {
  const { data: roles } = useGetPermitTemplateRolesByProject(
    { templateId, projectId },
    { enabled: !!templateId && !!projectId && !!participants.length },
  )

  const roleNameById = PermitTemplateRole.toNameById(roles)

  if (!participants.length) return null

  return (
    <Card sx={{ p: 2, mb: 2, ...sx }}>
      <Section label={localizedStrings.selectedParticipants}>
        <Text fontSize={12} color='text.secondary' sx={{ mt: 0.5, mb: 1.5 }}>
          {localizedStrings.participantsAddedAfterApproval}
        </Text>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {participants.map((participant) => {
            const roleName = roleNameById[participant.roleId]
            return (
              <Flex
                key={`${participant.firstName}_${participant.lastName}_${participant.company}_${participant.roleId}`}
                alignItems='center'
                sx={{ gap: 1.5 }}
              >
                <PersonOutlineIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Text fontSize={14}>
                    {participant.firstName} {participant.lastName}
                    {roleName ? ` - ${roleName}` : ''}
                  </Text>
                  {!!participant.company && (
                    <Text fontSize={12} color='text.secondary'>
                      {participant.company}
                    </Text>
                  )}
                </Box>
              </Flex>
            )
          })}
        </Box>
      </Section>
    </Card>
  )
}
